import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import LotteryResults from './pages/LotteryResults';
import PlaceBet from './pages/PlaceBet';
import History from './pages/History';
import Withdrawal from './pages/Withdrawal';
import Reports from './pages/Reports';
import BackOffice from './pages/BackOffice';
import UserTransactionHistory from './pages/UserTransactionHistory';
import EnhancedBetPage from './components/EnhancedBetPage';
import MobileBetPage from './components/MobileBetPage';
import PayoutRulesDashboard from './components/PayoutRulesDashboard';
import DownlineManagement from './components/DownlineManagement';
import CommissionManagementPage from './components/CommissionManagementPage';
import HierarchicalCommissionManagementWithTree from './components/HierarchicalCommissionManagementWithTree';
import SimpleDownlineManagement from './components/SimpleDownlineManagement';
import MultiLevelAccessManagement from './components/MultiLevelAccessManagement';
import HierarchicalCommissionManagement from './components/HierarchicalCommissionManagement';
import SalesManagement from './components/SalesManagement';
import WalletDebug from './pages/WalletDebug';
import UserSettlementReport from './components/UserSettlementReport';
import UserSettlementDetail from './pages/UserSettlementDetail';
import UserSalesReport from './components/UserSalesReport';
import Navbar from './components/Navbar';
import './App.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime)
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: 2,
      },
    },
  });

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

function AppContent() {
  const { isAuthenticated, user, isLoading } = useAuth();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {isAuthenticated && <Navbar />}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Routes>
          <Route path="/login" element={
            isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
              </Box>
            ) : isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <Login />
            )
          } />
          <Route path="/" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/results" element={
            <ProtectedRoute>
              <LotteryResults />
            </ProtectedRoute>
          } />
          <Route path="/bet" element={
            <ProtectedRoute>
              <PlaceBet />
            </ProtectedRoute>
          } />
          <Route path="/enhanced-bet" element={
            <ProtectedRoute>
              <EnhancedBetPage />
            </ProtectedRoute>
          } />
          <Route path="/mobile-bet" element={
            <ProtectedRoute>
              <MobileBetPage />
            </ProtectedRoute>
          } />
          <Route path="/history" element={
            <ProtectedRoute>
              <History />
            </ProtectedRoute>
          } />
          <Route path="/withdrawal" element={
            <ProtectedRoute>
              <Withdrawal />
            </ProtectedRoute>
          } />
          <Route path="/downline-management" element={
            <ProtectedRoute>
              <DownlineManagement />
            </ProtectedRoute>
          } />
          <Route path="/commission-management" element={
            <ProtectedRoute>
              <CommissionManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/hierarchical-commission" element={
            <ProtectedRoute>
              <HierarchicalCommissionManagement />
            </ProtectedRoute>
          } />
          <Route path="/sales-management" element={
            <ProtectedRoute>
              <SalesManagement />
            </ProtectedRoute>
          } />
          <Route path="/settlement-report" element={
            <ProtectedRoute>
              <UserSettlementReport />
            </ProtectedRoute>
          } />
          <Route path="/wallet-debug" element={
            <ProtectedRoute>
              <WalletDebug />
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute>
              <Reports />
            </ProtectedRoute>
          } />
          <Route path="/settlement/user/:userId" element={
            <ProtectedRoute>
              <UserSettlementDetail />
            </ProtectedRoute>
          } />
          {user?.role === 'admin' && (
            <Route path="/backoffice" element={
              <ProtectedRoute>
                <BackOffice />
              </ProtectedRoute>
            } />
          )}
          {user?.role === 'admin' && (
            <Route path="/admin/user-transactions" element={
              <ProtectedRoute>
                <UserTransactionHistory />
              </ProtectedRoute>
            } />
          )}
          {user?.role === 'admin' && (
            <Route path="/admin/payout-rules" element={
              <ProtectedRoute>
                <PayoutRulesDashboard />
              </ProtectedRoute>
            } />
          )}
          {user?.role === 'admin' && (
            <Route path="/admin/user-sales-report" element={
              <ProtectedRoute>
                <UserSalesReport />
              </ProtectedRoute>
            } />
          )}
        </Routes>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <AuthProvider>
            <Router>
              <AppContent />
            </Router>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
